import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import FormHelperText from '@mui/material/FormHelperText';
import DeleteIcon from '@mui/icons-material/Delete';

import ModalTemplate from './_ModalTemplate';
import SimpleSelect from '../Components/SimpleSelect';
import SimpleTextInput from '../Components/SimpleTextInput';
import { GBENSContext } from '../GBENSContainer';

export const TransportDocumentsModal = ({ open, setOpen, screen }) => {
  const { data, setData, referenceData, errors, checkErrorsStandard, isReadOnly } = useContext(GBENSContext);
  const modal = 'TransportDocuments';
  const modalLocal = '_' + modal;
  const [errorText, setErrorText] = useState(null);
  const [toCheckErrors, setToCheckErrors] = useState(false);

  const _error = useMemo(() => {
    return errors?.[screen]?.[modal]?.rows ? errors[screen][modal].rows : null;
  }, [errors, screen, open]);

  const newID = useCallback(() => {
    return data[screen][modal].rows.reduce((p, c) => {
      return Math.max(p, c.id + 1);
    }, 0);
  }, [data]);

  const validate = () => {
    if (!data?.[screen]?.[modalLocal]?.documentReference || data?.[screen]?.[modalLocal]?.documentReference === '') {
      setErrorText('Document Reference is required');
      return false;
    }
    if (!data?.[screen]?.[modalLocal]?.documentType || data?.[screen]?.[modalLocal]?.documentType === -1) {
      setErrorText('Document Type is required');
      return false;
    }
    setErrorText(null);
    return true;
  };

  const addRow = () => {
    const v = validate();
    if (!v) return;
    setData((prev) => ({
      ...prev,
      [screen]: {
        ...prev[screen],
        [modal]: {
          ...prev[screen][modal],
          rows: [
            ...prev[screen][modal].rows,
            {
              id: newID(),
              documentReference: prev[screen][modalLocal].documentReference,
              type: prev[screen][modalLocal].documentType,
            },
          ],
        },
        [modalLocal]: {
          documentReference: '',
          documentType: -1,
        },
      },
    }));
    setToCheckErrors(true);
  };

  useEffect(() => {
    if (toCheckErrors) {
      checkErrorsStandard('AdditionalDetails');
      setToCheckErrors(false);
    }
  }, [toCheckErrors]);

  const deleteRow = (id) => {
    setData((prev) => ({
      ...prev,
      [screen]: {
        ...prev[screen],
        [modal]: {
          ...prev[screen][modal],
          rows: [...prev[screen][modal].rows].filter((r) => r.id !== id),
        },
      },
    }));
  };

  const columns = [
    { field: 'documentReference', headerName: 'Reference', width: 200 },
    {
      field: 'type',
      headerName: 'Type',
      width: 200,
      renderCell: (params) => {
        const docType = referenceData.getDocumentType.find((doc) => String(doc.key) === String(params.value));
        return docType ? docType.value : '--';
      },
    },
    {
      field: 'delete',
      headerName: 'Delete',
      width: 100,
      type: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            key={id}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => deleteRow(id)}
            disabled={isReadOnly}
          />,
        ];
      },
    },
  ];

  const rowProps = {
    leftColumnWidth: '40%',
    rightColumnWidth: '60%',
  };

  return (
    <ModalTemplate
      open={open}
      onClose={() => setOpen(false)}
      title="Transport Document(s)"
      body={
        <>
          <div>
            <DataGrid rows={data[screen][modal].rows} columns={columns} />
            {_error && <FormHelperText style={{ color: '#d32f2f' }}>{_error.msg}</FormHelperText>}
            {errorText && (
              <Alert variant="filled" severity="error">
                {errorText}
              </Alert>
            )}
          </div>
          <div>
            <Typography id="modal-modal-title" variant="h6" component="h2" style={{ marginTop: 20 }}>
              Add a Transport Document
            </Typography>
            <SimpleTextInput
              name="Document Reference"
              modal={modalLocal}
              screen={screen}
              id="documentReference"
              rowProps={rowProps}
              disabled={isReadOnly}
            />
            <SimpleSelect
              name="Document Type"
              modal={modalLocal}
              screen={screen}
              id="documentType"
              options={referenceData.getDocumentType}
              rowProps={rowProps}
              disabled={isReadOnly}
            />
          </div>
        </>
      }
      buttons={[
        <Button key={0} onClick={() => setOpen(false)}>
          Close
        </Button>,
        <Button key={1} onClick={addRow} disabled={isReadOnly}>
          Add Document
        </Button>,
      ]}
    />
  );
};

TransportDocumentsModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
};
